// src/services/adminService.js
const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
const getToken = () => localStorage.getItem('nlngra_token');

const handleResponse = async(response) => {
    const text = await response.text();
    console.log('Raw response:', text); // Debug log

    const jsonMatch = text.match(/\{.*\}/s);
    if (jsonMatch) {
        try {
            return JSON.parse(jsonMatch[0]);
        } catch (e) {
            console.error('JSON parse error:', e);
            throw new Error('Invalid JSON format');
        }
    }
    throw new Error('Invalid response format');
};

export const adminService = {
    // ============================================
    // Existing Methods
    // ============================================

    // Get admin dashboard stats - FIXED (removed index.php?url=)
    getAdminStats: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin_stats.php?_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get admin stats error:', error);
            return { success: false };
        }
    },

    // Get all bookings (admin)
    getAllBookings: async(filters = {}) => {
        try {
            const params = new URLSearchParams({
                action: 'admin_list',
                _t: Date.now(),
                ...filters
            }).toString();

            const response = await fetch(`${API_BASE_URL}/bookings.php?${params}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get all bookings error:', error);
            return { success: false, bookings: [] };
        }
    },

    // Update booking status
    updateBookingStatus: async(bookingId, status, reason = null, newLocation = null) => {
        try {
            const response = await fetch(`${API_BASE_URL}/bookings.php?action=admin_update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    booking_id: bookingId,
                    status: status,
                    reason: reason,
                    new_location: newLocation
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Update booking status error:', error);
            throw error;
        }
    },

    // Get all section membership requests (admin)
    getSectionRequests: async(status = 'pending') => {
        try {
            console.log(`Fetching section requests with status: ${status}`);
            const response = await fetch(`${API_BASE_URL}/sections.php?action=admin_list&status=${status}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            const result = await handleResponse(response);
            console.log('Section requests response:', result);
            return result;
        } catch (error) {
            console.error('Get section requests error:', error);
            return { success: false, requests: [] };
        }
    },

    // Approve/reject section membership
    updateSectionRequest: async(requestId, status, reason = null) => {
        try {
            console.log(`Updating section request ${requestId} to status: ${status}`);
            const response = await fetch(`${API_BASE_URL}/sections.php?action=admin_approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    request_id: requestId,
                    status: status,
                    reason: reason
                })
            });
            const result = await handleResponse(response);
            console.log('Update section request response:', result);
            return result;
        } catch (error) {
            console.error('Update section request error:', error);
            throw error;
        }
    },

    // Get all pending members (admin) - FIXED to use index.php endpoints
    getPendingMembers: async() => {
        try {
            // Use the correct endpoint from index.php: /pending-users
            const response = await fetch(`${API_BASE_URL}/pending-users?_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            const result = await handleResponse(response);

            // Transform the response to match what the frontend expects
            // The /pending-users endpoint returns { success: true, users: [...] }
            return {
                success: result.success,
                users: result.users || []
            };
        } catch (error) {
            console.error('Get pending members error:', error);
            return { success: false, users: [] };
        }
    },

    // Approve/reject member registration - FIXED to use index.php endpoints
    updateMemberStatus: async(userId, action, reason = null) => {
        try {
            // Use the correct endpoints from index.php
            let endpoint = '';
            let body = {};

            if (action === 'approve') {
                endpoint = `${API_BASE_URL}/approve-user`;
                body = { user_id: userId };
            } else if (action === 'reject' || action === 'disapprove') {
                endpoint = `${API_BASE_URL}/disapprove-user`;
                body = {
                    user_id: userId,
                    reason: reason || 'Registration rejected by admin'
                };
            } else {
                throw new Error('Invalid action: ' + action);
            }

            const response = await fetch(`${endpoint}?_t=${Date.now()}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(body)
            });

            const result = await handleResponse(response);

            // Transform response to match what frontend expects
            return {
                success: result.success,
                message: result.message || (action === 'approve' ? 'Member approved successfully' : 'Member rejected successfully')
            };
        } catch (error) {
            console.error('Update member status error:', error);
            throw error;
        }
    },
    // Get all users with status filter (alternative to getPendingMembers)
    getUsersByStatus: async(status = 'all') => {
        try {
            let endpoint = '';
            switch (status) {
                case 'pending':
                    endpoint = `${API_BASE_URL}/pending-users`;
                    break;
                case 'active':
                    endpoint = `${API_BASE_URL}/active-users`;
                    break;
                case 'suspended':
                    endpoint = `${API_BASE_URL}/suspended-users`;
                    break;
                default:
                    endpoint = `${API_BASE_URL}/all-users`;
            }

            const response = await fetch(`${endpoint}?_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error(`Get ${status} users error:`, error);
            return { success: false, users: [] };
        }
    },
    // Get audit logs - FIXED
    getAuditLogs: async(limit = 100) => {
        try {
            const response = await fetch(`${API_BASE_URL}/audit.php?action=list&limit=${limit}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get audit logs error:', error);
            return { success: false, logs: [] };
        }
    },

    // Send bulk communication - FIXED
    sendBulkCommunication: async(type, recipients, message, subject = null) => {
        try {
            const response = await fetch(`${API_BASE_URL}/communication.php?action=bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    type: type,
                    recipients: recipients,
                    message: message,
                    subject: subject
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Send bulk communication error:', error);
            throw error;
        }
    },

    // Get waitlist bookings
    getWaitlistBookings: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/bookings.php?action=list&status=waitlist&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get waitlist bookings error:', error);
            return { success: false, bookings: [] };
        }
    },

    // Promote from waitlist
    promoteFromWaitlist: async(bookingId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/bookings.php?action=admin_update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    booking_id: bookingId,
                    status: 'pending'
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Promote from waitlist error:', error);
            throw error;
        }
    },

    // Get all feedback - FIXED
    getFeedback: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/feedback.php?action=list&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get feedback error:', error);
            return { success: true, feedbacks: [] };
        }
    },

    // Respond to feedback - FIXED
    respondToFeedback: async(feedbackId, response) => {
        try {
            const apiResponse = await fetch(`${API_BASE_URL}/feedback.php?action=respond`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    feedback_id: feedbackId,
                    response: response
                })
            });
            return await handleResponse(apiResponse);
        } catch (error) {
            console.error('Respond to feedback error:', error);
            throw error;
        }
    },

    // Create announcement - FIXED
    createAnnouncement: async(announcement) => {
        try {
            const response = await fetch(`${API_BASE_URL}/announcements.php?action=create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(announcement)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Create announcement error:', error);
            throw error;
        }
    },

    // Delete announcement - FIXED
    deleteAnnouncement: async(announcementId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/announcements.php?action=delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id: announcementId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete announcement error:', error);
            throw error;
        }
    },

    // Schedule maintenance - FIXED
    scheduleMaintenance: async(maintenanceData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/maintenance.php?action=schedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(maintenanceData)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Schedule maintenance error:', error);
            throw error;
        }
    },

    // Cancel maintenance - FIXED
    cancelMaintenance: async(maintenanceId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/maintenance.php?action=cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id: maintenanceId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Cancel maintenance error:', error);
            throw error;
        }
    },

    // Add blackout date - FIXED
    addBlackoutDate: async(blackoutData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/blackout.php?action=add`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(blackoutData)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Add blackout date error:', error);
            throw error;
        }
    },

    // Remove blackout date - FIXED
    removeBlackoutDate: async(blackoutId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/blackout.php?action=remove`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id: blackoutId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Remove blackout date error:', error);
            throw error;
        }
    },

    // Add pricing rule - FIXED
    addPricingRule: async(pricingData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/pricing.php?action=add`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(pricingData)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Add pricing rule error:', error);
            throw error;
        }
    },

    // Update pricing rule - FIXED
    updatePricingRule: async(ruleId, updates) => {
        try {
            const response = await fetch(`${API_BASE_URL}/pricing.php?action=update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id: ruleId, ...updates })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Update pricing rule error:', error);
            throw error;
        }
    },

    // Delete pricing rule - FIXED
    deletePricingRule: async(ruleId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/pricing.php?action=delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id: ruleId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete pricing rule error:', error);
            throw error;
        }
    },

    // Get all sections with member counts
    getAllSectionsWithCounts: async() => {
        try {
            console.log('Fetching sections from:', `${API_BASE_URL}/sections.php?action=get_all_sections_with_counts&_t=${Date.now()}`);
            const response = await fetch(`${API_BASE_URL}/sections.php?action=get_all_sections_with_counts&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            const result = await handleResponse(response);
            console.log('API Response:', result);
            return result;
        } catch (error) {
            console.error('Get all sections error:', error);
            return { success: false, sections: [] };
        }
    },

    // Update role permissions - FIXED
    updateRolePermissions: async(roleId, permissions) => {
        try {
            const response = await fetch(`${API_BASE_URL}/permissions.php?action=update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ role_id: roleId, permissions: permissions })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Update role permissions error:', error);
            throw error;
        }
    },

    // ============================================
    // NEW FACILITIES API METHODS
    // ============================================

    // Get all facilities from database
    getFacilities: async(category = null, maintenance = 'operational') => {
        try {
            let url = `${API_BASE_URL}/facilities.php?action=list&maintenance=${maintenance}&_t=${Date.now()}`;
            if (category) {
                url += `&category=${encodeURIComponent(category)}`;
            }
            const response = await fetch(url, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get facilities error:', error);
            return { success: false, facilities: [] };
        }
    },

    // Get facility categories
    getFacilityCategories: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/facilities.php?action=categories&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get facility categories error:', error);
            return { success: false, categories: [] };
        }
    },

    // Get single facility by ID
    getFacilityById: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/facilities.php?action=get&id=${id}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get facility by ID error:', error);
            return { success: false };
        }
    },

    // Get facility statistics
    getFacilityStats: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/facilities.php?action=stats&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get facility stats error:', error);
            return { success: false, stats: [] };
        }
    },

    // Search facilities
    searchFacilities: async(query) => {
        try {
            const response = await fetch(`${API_BASE_URL}/facilities.php?action=search&q=${encodeURIComponent(query)}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Search facilities error:', error);
            return { success: false, facilities: [] };
        }
    },

    // ============================================
    // NEW EQUIPMENT API METHODS
    // ============================================

    // Get all equipment
    getEquipment: async(sportingOnly = null, category = null) => {
        try {
            let url = `${API_BASE_URL}/equipment.php?action=list&_t=${Date.now()}`;
            if (sportingOnly !== null) {
                url += `&sporting_only=${sportingOnly}`;
            }
            if (category) {
                url += `&category=${encodeURIComponent(category)}`;
            }
            const response = await fetch(url, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get equipment error:', error);
            return { success: false, equipment: [] };
        }
    },

    // Get equipment categories
    getEquipmentCategories: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment.php?action=categories&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get equipment categories error:', error);
            return { success: false, categories: [] };
        }
    },

    // Get equipment by category
    getEquipmentByCategory: async(category) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment.php?action=by_category&category=${encodeURIComponent(category)}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get equipment by category error:', error);
            return { success: false, equipment: [] };
        }
    },

    // Get equipment stats
    getEquipmentStats: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment.php?action=stats&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get equipment stats error:', error);
            return { success: false, stats: [] };
        }
    },

    // Get single equipment item
    getEquipmentById: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment.php?action=get&id=${encodeURIComponent(id)}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get equipment by ID error:', error);
            return { success: false };
        }
    },

    // ============================================
    // NEW ROLES & PERMISSIONS API METHODS
    // ============================================

    // Get all roles
    getRoles: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/roles.php?action=list&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get roles error:', error);
            return { success: false, roles: [] };
        }
    },

    // Get available users for delegation
    getAvailableUsers: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/roles.php?action=available_users&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get available users error:', error);
            return { success: false, users: [] };
        }
    },

    // Get user role by email
    getUserRole: async(email) => {
        try {
            const response = await fetch(`${API_BASE_URL}/roles.php?action=user_role&email=${encodeURIComponent(email)}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get user role error:', error);
            return { success: false };
        }
    },

    // Get current user's permissions
    getMyPermissions: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/roles.php?action=my_permissions&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get permissions error:', error);
            return { success: false };
        }
    },

    // ============================================
    // NEW LOOKUP DATA API METHODS
    // ============================================

    // Get all lookup data
    getLookupData: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/lookup.php?action=all&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get lookup data error:', error);
            return { success: false, data: {} };
        }
    },

    // Get jersey sizes
    getJerseySizes: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/lookup.php?action=jersey_sizes&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get jersey sizes error:', error);
            return { success: false, sizes: [] };
        }
    },

    // Get time slots
    getTimeSlots: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/lookup.php?action=time_slots&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get time slots error:', error);
            return { success: false, slots: [] };
        }
    },

    // Get event types
    getEventTypes: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/lookup.php?action=event_types&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get event types error:', error);
            return { success: false, event_types: [] };
        }
    },

    // Get booking statuses
    getBookingStatuses: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/lookup.php?action=booking_statuses&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get booking statuses error:', error);
            return { success: false, booking_statuses: [] };
        }
    },

    // ============================================
    // USER MANAGEMENT API METHODS (KEPT AS IS)
    // ============================================

    // Get all users with pagination and filters
    getAllUsers: async(filters = {}) => {
        try {
            const params = new URLSearchParams({
                status: filters.status || 'all',
                role: filters.role || 'all',
                search: filters.search || '',
                limit: filters.limit || 50,
                offset: filters.offset || 0,
                _t: Date.now()
            }).toString();

            const response = await fetch(`${API_BASE_URL}/users.php?${params}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get all users error:', error);
            return { success: false, users: [] };
        }
    },

    // Get user by ID
    getUserById: async(userId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=get&id=${userId}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get user by ID error:', error);
            throw error;
        }
    },

    // Add this improved updateUser method
    updateUser: async(userId, userData) => {
        console.log('updateUser called with:', { userId, userData });

        // Make sure account_type is included
        if (!userData.account_type) {
            console.warn('WARNING: account_type not provided in userData!');
        }

        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    user_id: userId,
                    first_name: userData.first_name || '',
                    last_name: userData.last_name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    account_type: userData.account_type || 'member',
                    reason: userData.reason || 'Role updated by admin'
                })
            });

            const result = await response.json();
            console.log('updateUser result:', result);

            if (!result.success) {
                throw new Error(result.message || 'Failed to update user');
            }

            return result;
        } catch (error) {
            console.error('Update user error:', error);
            throw error;
        }
    },


    // Add this test method to debug
    testRoleUpdate: async(userId, role) => {
        console.log('Testing role update:', { userId, role });

        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=test_update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    user_id: userId,
                    role: role
                })
            });

            const result = await response.json();
            console.log('Test result:', result);
            return result;
        } catch (error) {
            console.error('Test failed:', error);
            throw error;
        }
    },

    // Add method to get role change logs
    getRoleChangeLogs: async(limit = 100, offset = 0) => {
        try {
            const response = await fetch(`${API_BASE_URL}/index.php?url=role-change-logs&limit=${limit}&offset=${offset}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get role change logs error:', error);
            return { success: false, logs: [] };
        }
    },

    // Activate user
    activateUser: async(userId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ user_id: userId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Activate user error:', error);
            throw error;
        }
    },

    // Suspend user
    suspendUser: async(userId, reason) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=suspend`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    user_id: userId,
                    reason: reason || 'Violation of terms'
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Suspend user error:', error);
            throw error;
        }
    },

    // Delete user
    deleteUser: async(userId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ user_id: userId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete user error:', error);
            throw error;
        }
    },

    // Bulk approve users
    bulkApproveUsers: async(userIds) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=bulk_approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ user_ids: userIds })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Bulk approve users error:', error);
            throw error;
        }
    },

    // Bulk suspend users
    bulkSuspendUsers: async(userIds, reason) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=bulk_suspend`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    user_ids: userIds,
                    reason: reason || 'Bulk suspension'
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Bulk suspend users error:', error);
            throw error;
        }
    },

    // Bulk activate users
    bulkActivateUsers: async(userIds) => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=bulk_activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ user_ids: userIds })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Bulk activate users error:', error);
            throw error;
        }
    },

    // Get user statistics
    getUserStats: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=stats&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get user stats error:', error);
            return { success: false, stats: {} };
        }
    },

    // Get available user roles
    getUserRolesList: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/roles.php?action=list&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get user roles list error:', error);
            return { success: false, roles: [] };
        }
    },

    // Get active users count
    getActiveUsersCount: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=count&status=active&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get active users count error:', error);
            return { success: false, count: 0 };
        }
    },

    // Get pending users count
    getPendingUsersCount: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=count&status=pending&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get pending users count error:', error);
            return { success: false, count: 0 };
        }
    },

    // Get suspended users count
    getSuspendedUsersCount: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/users.php?action=count&status=suspended&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get suspended users count error:', error);
            return { success: false, count: 0 };
        }
    },

    // ============================================
    // ASSOCIATE MEMBER REVIEW METHODS
    // ============================================

    // Get pending associate members for review
    getPendingAssociates: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/get_pending_associates.php?_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get pending associates error:', error);
            return { success: false, pending: [], approved: [], rejected: [] };
        }
    },

    // Approve associate member
    approveAssociate: async(memberId, notes = '') => {
        try {
            const response = await fetch(`${API_BASE_URL}/approve_associate.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    member_id: memberId,
                    notes: notes
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Approve associate error:', error);
            throw error;
        }
    },

    // Reject associate member
    rejectAssociate: async(memberId, reason) => {
        try {
            const response = await fetch(`${API_BASE_URL}/reject_associate.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    member_id: memberId,
                    reason: reason
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Reject associate error:', error);
            throw error;
        }
    },

    // ============================================
    // ANALYTICS API METHODS
    // ============================================

    // Get booking analytics
    getBookingAnalytics: async(period = 'month', startDate = null, endDate = null) => {
        try {
            let url = `${API_BASE_URL}/analytics.php?action=bookings&period=${period}&_t=${Date.now()}`;
            if (startDate && endDate) {
                url += `&start_date=${startDate}&end_date=${endDate}`;
            }
            const response = await fetch(url, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get booking analytics error:', error);
            return { success: false };
        }
    },

    // Get section analytics
    getSectionAnalytics: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/analytics.php?action=sections&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get section analytics error:', error);
            return { success: false };
        }
    },

    // Get member analytics
    getMemberAnalytics: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/analytics.php?action=members&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get member analytics error:', error);
            return { success: false };
        }
    },

    // Update user role with logging
    updateUserRole: async(userId, newRole, reason = '') => {
        try {
            const userResponse = await fetch(`${API_BASE_URL}/users.php?action=get&id=${userId}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            const userData = await handleResponse(userResponse);

            if (!userData.success || !userData.user) {
                throw new Error('Could not fetch current user data');
            }

            const oldRole = userData.user.account_type;

            const updateResponse = await fetch(`${API_BASE_URL}/users.php?action=update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    user_id: userId,
                    account_type: newRole
                })
            });
            const updateResult = await handleResponse(updateResponse);

            if (!updateResult.success) {
                throw new Error(updateResult.message || 'Failed to update role');
            }

            const logResponse = await fetch(`${API_BASE_URL}/log_role_change.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    target_user: userId,
                    old_role: oldRole,
                    new_role: newRole,
                    reason: reason
                })
            });

            const logResult = await handleResponse(logResponse);

            if (!logResult.success) {
                console.warn('Role change succeeded but logging failed:', logResult.message);
            }

            return { success: true, message: 'Role updated successfully' };

        } catch (error) {
            console.error('Update user role error:', error);
            throw error;
        }
    },

    // ============================================
    // EQUIPMENT CRUD METHODS
    // ============================================

    // List all equipment
    listEquipment: async(category = null, sportingOnly = null) => {
        try {
            let url = `${API_BASE_URL}/equipment_crud.php?action=list_equipment&_t=${Date.now()}`;
            if (category) url += `&category=${encodeURIComponent(category)}`;
            if (sportingOnly !== null) url += `&sporting_only=${sportingOnly}`;

            const response = await fetch(url, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('List equipment error:', error);
            return { success: false, data: [] };
        }
    },

    // Get single equipment
    getEquipmentItem: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=get_equipment&id=${encodeURIComponent(id)}&_t=${Date.now()}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get equipment error:', error);
            return { success: false };
        }
    },

    // Create equipment
    createEquipment: async(data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=create_equipment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(data)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Create equipment error:', error);
            throw error;
        }
    },

    // Update equipment
    updateEquipment: async(id, data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=update_equipment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id, ...data })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Update equipment error:', error);
            throw error;
        }
    },

    // Delete equipment
    deleteEquipment: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=delete_equipment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete equipment error:', error);
            throw error;
        }
    },

    // Get equipment categories
    getEquipmentCategoriesList: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=get_equipment_categories&_t=${Date.now()}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get equipment categories error:', error);
            return { success: false, data: [] };
        }
    },

    // ============================================
    // SECTIONS CRUD METHODS
    // ============================================

    // List all sections
    listSections: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=list_sections&_t=${Date.now()}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('List sections error:', error);
            return { success: false, data: [] };
        }
    },

    // Get single section
    getSection: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=get_section&id=${id}&_t=${Date.now()}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get section error:', error);
            return { success: false };
        }
    },

    // Create section
    createSection: async(data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=create_section`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(data)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Create section error:', error);
            throw error;
        }
    },

    // Update section
    updateSection: async(id, data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=update_section`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id, ...data })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Update section error:', error);
            throw error;
        }
    },

    // Delete section
    deleteSection: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=delete_section`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete section error:', error);
            throw error;
        }
    },

    // ============================================
    // FACILITIES CRUD METHODS
    // ============================================

    // List all facilities
    listFacilities: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=list_facilities&_t=${Date.now()}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('List facilities error:', error);
            return { success: false, data: [] };
        }
    },

    // Get single facility
    getFacility: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=get_facility&id=${id}&_t=${Date.now()}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get facility error:', error);
            return { success: false };
        }
    },

    // Create facility
    createFacility: async(data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=create_facility`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(data)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Create facility error:', error);
            throw error;
        }
    },

    // Update facility
    updateFacility: async(id, data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=update_facility`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id, ...data })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Update facility error:', error);
            throw error;
        }
    },

    // Delete facility
    deleteFacility: async(id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=delete_facility`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ id })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Delete facility error:', error);
            throw error;
        }
    },

    // Get facility categories
    getFacilityCategoriesList: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/equipment_crud.php?action=get_facility_categories&_t=${Date.now()}`, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get facility categories error:', error);
            return { success: false, data: [] };
        }
    },

    // ============================================
    // ADVANCED ANALYTICS METHODS (FROM SQL)
    // ============================================

    // Get available years from bookings (from SQL database)
    getAvailableYears: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/analytics.php?action=get_available_years&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get available years error:', error);
            return { success: false, years: [] };
        }
    },

    // Get bookings by specific year from SQL
    getBookingsByYear: async(year) => {
        try {
            const response = await fetch(`${API_BASE_URL}/analytics.php?action=get_bookings_by_year&year=${year}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get bookings by year error:', error);
            return { success: false, bookings: [] };
        }
    },

    // Get bookings by month from SQL
    getBookingsByMonth: async(year, month) => {
        try {
            const response = await fetch(`${API_BASE_URL}/analytics.php?action=get_bookings_by_month&year=${year}&month=${month}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get bookings by month error:', error);
            return { success: false, bookings: [] };
        }
    },

    // Get bookings by quarter from SQL
    getBookingsByQuarter: async(year, quarter) => {
        try {
            const response = await fetch(`${API_BASE_URL}/analytics.php?action=get_bookings_by_quarter&year=${year}&quarter=${quarter}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get bookings by quarter error:', error);
            return { success: false, bookings: [] };
        }
    },

    // Get date range from SQL (min and max dates from bookings)
    getDateRange: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/analytics.php?action=get_date_range&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get date range error:', error);
            return { success: false, minDate: null, maxDate: null };
        }
    }
};